// api/reports.ts — typed-обёртка над endpoints отчётов (V14 W9, §3.6; FE-10, §5.2).
// Контракт (backend ReportResponse.java/ReportRow, ReportsController.java):
//   - GET /api/reports/{reportId}?from=&to=&departmentId=&inactiveDays=
//     → ReportResponse { reportId, generatedAt, rows, meta { period, scope, truncated } };
//   - GET /api/reports/{reportId}/xlsx?... → blob (attachment,
//     filename="report-<slug>-<yyyyMMdd>.xlsx" — имя только ASCII, канон BE).
// Каталог отчётов (REPORTS) — FE-статика (Q1-решение, ТЗ §3.6): BE отвечает за данные,
// витрину (названия/порядок/флаги) несёт UI; серверный GET /api/reports — опционален.

import { apiClient } from './client';

/** Slug отчёта (база API-имён: ReportType.fromApi, case-sensitive). */
export type ReportId =
  | 'funnel'
  | 'lead-conversion'
  | 'revenue'
  | 'by-managers'
  | 'monthly-dynamics'
  | 'risks';

/** Параметры запроса отчёта (все опциональны; правила — на BE, §3.5). */
export interface ReportParams {
  /** Начало периода (ISO Instant, включительно). */
  from?: string;
  /** Конец периода (ISO Instant, включительно). */
  to?: string;
  /** Фильтр департамента: admin-only; для lead-conversion запрещён (400). */
  departmentId?: string;
  /** Порог без активности для R6 («risks»): 1..90, дефолт 7 на BE. */
  inactiveDays?: number;
}

/** Метаданные ответа отчёта (ReportMeta: period/scope/truncated). */
export interface ReportMeta {
  /** Фактический период данных (null-поля = as-of). */
  period: { from: string | null; to: string | null };
  /** Описатель применённого RBAC-скоупа: ALL/DEPARTMENTS/OWNER. */
  scope: string;
  /** true = R6 усечён до 5000 строк (B2: FE предупреждает). */
  truncated: boolean;
}

/** Строка R1 «Воронка»: стадия + валюта + кол-во + сумма (SQL-порядок воронки). */
export interface FunnelRow {
  stage: string;
  currency: string;
  dealCount: number;
  amount: number;
}

/** Строка R2 «Конверсия лид→сделка»: пивот по источнику (count/converted/%). */
export interface LeadConversionRow {
  source: string;
  totalCount: number;
  convertedCount: number;
  convertedPct: number;
}

/** Сумма с кол-вом по валюте (секции R3-факта). */
export interface CurrencyTotal {
  currency: string;
  count: number;
  amount: number;
}

/** Прогнозная строка R3: стадия + валюта + кол-во + сумма. */
export interface StageTotal {
  stage: string;
  currency: string;
  count: number;
  amount: number;
}

/** Строка R3 «Выручка»: факт (payments/wonDeals) + прогноз (общий «итог» не выводится — I4). */
export interface RevenueRow {
  actual: { payments: CurrencyTotal[]; wonDeals: CurrencyTotal[] };
  forecast: StageTotal[];
}

/** Строка R4 «Сделки по менеджерам»: владелец + департамент + 4 показателя. */
export interface ManagersRow {
  ownerUserId: string;
  ownerName: string;
  departmentId: string | null;
  departmentName: string | null;
  currency: string;
  dealCount: number;
  activeAmount: number;
  wonCount: number;
  wonAmount: number;
}

/** Строка R5 «Динамика по месяцам»: слияние 3 агрегатов по ключу month|currency. */
export interface MonthlyRow {
  /** LocalDate «yyyy-MM-dd» (месяц: первый день). */
  month: string;
  currency: string;
  dealsCreatedCount: number;
  dealsCreatedAmount: number;
  wonCount: number;
  wonAmount: number;
  paymentsCount: number;
  paymentsAmount: number;
}

/** Причина попадания сделки в отчёт «Риски» (RiskReason, §3.2/§3.8). */
export type RiskReason = 'NO_NEXT_CONTACT' | 'OVERDUE_NEXT_CONTACT' | 'STALE';

/** Строка R6 «Риски»: сделка + окно контактов + причина (снимок на дату отчёта). */
export interface RiskRow {
  dealId: string;
  title: string;
  stage: string;
  amount: number;
  currency: string;
  updatedAt: string;
  ownerName: string;
  departmentName: string | null;
  nextContactAt: string | null;
  lastContactAt: string | null;
  inactiveDays: number;
  riskReason: RiskReason;
}

/** Строка отчёта (полиморфная сериализация backend-записей без @JsonTypeInfo). */
export type ReportRow =
  | FunnelRow
  | LeadConversionRow
  | RevenueRow
  | ManagersRow
  | MonthlyRow
  | RiskRow;

/** Конверт ответа отчёта (ReportResponse, §3.3 решение-дефолт №4). */
export interface ReportResponse {
  reportId: string;
  generatedAt: string;
  rows: ReportRow[];
  meta: ReportMeta;
}

/** Описание отчёта в статическом FE-каталоге (Q1). Флаги управляют UI-условиями. */
export interface ReportDescriptor {
  id: ReportId;
  /** i18n-ключ названия (значения — W10: `reports.report.<id>.title`). */
  titleKey: string;
  /** i18n-ключ подписи-описания. */
  descriptionKey: string;
  /** true — показывать пикер периода (as-of отчёты funnel/risks — false). */
  usePeriod: boolean;
  /** true — допустим деп-фильтр (admin; lead-conversion — false, 400 на BE). */
  useDepartment: boolean;
  /** true — пикер порога без активности (только risks). */
  useInactiveDays: boolean;
}

/** FE-каталог отчётов (6, порядок ТЗ §3.2). */
export const REPORTS: readonly ReportDescriptor[] = [
  { id: 'funnel', titleKey: 'reports.report.funnel.title', descriptionKey: 'reports.report.funnel.description', usePeriod: false, useDepartment: true, useInactiveDays: false },
  { id: 'lead-conversion', titleKey: 'reports.report.lead-conversion.title', descriptionKey: 'reports.report.lead-conversion.description', usePeriod: true, useDepartment: false, useInactiveDays: false },
  { id: 'revenue', titleKey: 'reports.report.revenue.title', descriptionKey: 'reports.report.revenue.description', usePeriod: true, useDepartment: true, useInactiveDays: false },
  { id: 'by-managers', titleKey: 'reports.report.by-managers.title', descriptionKey: 'reports.report.by-managers.description', usePeriod: true, useDepartment: true, useInactiveDays: false },
  { id: 'monthly-dynamics', titleKey: 'reports.report.monthly-dynamics.title', descriptionKey: 'reports.report.monthly-dynamics.description', usePeriod: true, useDepartment: true, useInactiveDays: false },
  { id: 'risks', titleKey: 'reports.report.risks.title', descriptionKey: 'reports.report.risks.description', usePeriod: false, useDepartment: true, useInactiveDays: true },
];

/** Результат XLSX-выгрузки: blob + имя файла (для downloadBlob). */
export interface XlsxDownload {
  blob: Blob;
  filename: string;
}

/** Имя файла по умолчанию: report-<id>-<yyyyMMdd>.xlsx (зеркало BE-контракта). */
function fallbackFilename(id: ReportId): string {
  const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  return `report-${id}-${today}.xlsx`;
}

/**
 * Извлекает filename из Content-Disposition (attachment; filename="...");
 * нет заголовка/имени — fallback (канон BE: имя всегда задаётся, fallback — страховка).
 */
function resolveFilename(disposition: string | null | undefined, id: ReportId): string {
  const match = disposition?.match(/filename="?([^";]+)"?/i);
  return match?.[1] ? match[1] : fallbackFilename(id);
}

/** Reports API (V14 W9). */
export const reportsApi = {
  /**
   * GET /api/reports/{id} — данные отчёта (JSON). Params передаются только
   * непустыми: axios сам пропускает undefined — as-of отчёты уходят без from/to.
   */
  async getReport(id: ReportId, params: ReportParams = {}): Promise<ReportResponse> {
    const { data } = await apiClient.get<ReportResponse>(`/api/reports/${id}`, { params });
    return data;
  },

  /**
   * GET /api/reports/{id}/xlsx — выгрузка в XLSX (blob; данные/скоуп на сервере).
   * Имя файла — из Content-Disposition (attachment filename="report-...-yyyyMMdd.xlsx").
   */
  async downloadXlsx(id: ReportId, params: ReportParams = {}): Promise<XlsxDownload> {
    const response = await apiClient.get<Blob>(`/api/reports/${id}/xlsx`, {
      params,
      responseType: 'blob',
    });
    return {
      blob: response.data,
      filename: resolveFilename(response.headers['content-disposition'], id),
    };
  },
};
